/**
 * Platform Admin Authentication Context
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { platformClient, PlatformAdmin } from '../api/platformClient';
import { jwtDecode } from 'jwt-decode';

interface AuthState {
  admin: PlatformAdmin | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'platform_access_token';
const REFRESH_TOKEN_KEY = 'platform_refresh_token';

interface JWTPayload {
  exp: number;
  sub: string;
  email: string;
  role: string;
}

function isTokenExpired(token: string): boolean {
  try {
    const decoded = jwtDecode<JWTPayload>(token);
    return decoded.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

export function PlatformAuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    admin: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Initialize auth state from stored token
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (!token) {
        setState({ admin: null, isAuthenticated: false, isLoading: false });
        return;
      }

      // Check if token is expired
      if (isTokenExpired(token)) {
        if (refreshToken) {
          try {
            const tokens = await platformClient.refreshToken(refreshToken);
            localStorage.setItem(TOKEN_KEY, tokens.accessToken);
            localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
            platformClient.setToken(tokens.accessToken);
          } catch {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            setState({ admin: null, isAuthenticated: false, isLoading: false });
            return;
          }
        } else {
          localStorage.removeItem(TOKEN_KEY);
          setState({ admin: null, isAuthenticated: false, isLoading: false });
          return;
        }
      } else {
        platformClient.setToken(token);
      }

      // Fetch current admin
      try {
        const admin = await platformClient.getCurrentAdmin();
        setState({ admin, isAuthenticated: true, isLoading: false });
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        platformClient.setToken(null);
        setState({ admin: null, isAuthenticated: false, isLoading: false });
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { admin, tokens } = await platformClient.login(email, password);
    localStorage.setItem(TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    platformClient.setToken(tokens.accessToken);
    setState({ admin, isAuthenticated: true, isLoading: false });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    platformClient.setToken(null);
    setState({ admin: null, isAuthenticated: false, isLoading: false });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function usePlatformAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('usePlatformAuth must be used within PlatformAuthProvider');
  }
  return context;
}
